import React, { memo } from 'react';
import { FileText, ExternalLink, Building2 } from 'lucide-react';
import useObjectDrawerStore from '../../store/drawer/objectDrawerStore';

interface ArtifactCardProps {
  data: {
    doc_external_id?: string;
    doc_id?: string;
    title?: string;
    doc_type?: string;
    status?: string;
    account_external_ids?: string[];
    error?: string;
  };
}

const DOC_TYPE_LABELS: Record<string, string> = {
  ai_artifact: 'AI Artifact',
  account_review: 'Account Review',
  action_plan: 'Action Plan',
  research_summary: 'Research Summary',
  health_assessment: 'Health Assessment',
  qbr_prep: 'QBR Prep',
  free_form: 'Document',
};

const ArtifactCard: React.FC<ArtifactCardProps> = memo(({ data }) => {
  const openDrawer = useObjectDrawerStore((s) => s.openDrawer);

  if (data.error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        Failed to save artifact
      </div>
    );
  }

  const handleClick = () => {
    if (data.doc_external_id) {
      openDrawer({
        objectType: 'doc',
        objectId: data.doc_external_id,
      });
    }
  };

  const docTypeLabel = DOC_TYPE_LABELS[data.doc_type || ''] || data.doc_type || 'Document';
  const accountCount = data.account_external_ids?.length || 0;

  return (
    <div
      onClick={handleClick}
      className="group relative cursor-pointer overflow-hidden rounded-xl border border-indigo-200 bg-gradient-to-r from-indigo-50/80 to-purple-50/80 p-4 shadow-sm transition-all duration-200 hover:shadow-md hover:border-indigo-300 hover:from-indigo-50 hover:to-purple-50"
    >
      {/* Gradient accent bar */}
      <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-indigo-500 to-purple-500 rounded-l-xl" />

      <div className="flex items-start gap-3 pl-2">
        {/* Icon */}
        <div className="flex-shrink-0 flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 text-white shadow-sm">
          <FileText size={18} />
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-slate-900 truncate leading-tight">
              {data.title || 'Untitled Artifact'}
            </h4>
            <ExternalLink
              size={14}
              className="flex-shrink-0 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity"
            />
          </div>

          <div className="flex items-center gap-2 mt-1.5">
            {/* Doc type badge */}
            <span className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-medium text-indigo-700">
              {docTypeLabel}
            </span>

            {/* Draft badge */}
            <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">
              Draft
            </span>

            {/* Linked accounts */}
            {accountCount > 0 && (
              <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                <Building2 size={12} />
                {accountCount} {accountCount === 1 ? 'account' : 'accounts'}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

ArtifactCard.displayName = 'ArtifactCard';

export default ArtifactCard;
